import { dds } from "./ddsClient";

/**
 * A ref-count of the DDS subscriptions this app is holding RIGHT NOW.
 *
 * WHY THIS EXISTS
 * ---------------
 * The live-data banner used to latch on "Reconnecting…" forever, on pages that
 * never opened a socket at all. The cause is a collaboration between two
 * correct-in-isolation behaviours:
 *
 *   1. The graphql-ws client is `lazy: true`. When the last subscription is
 *      disposed it closes the socket ITSELF, with a clean 1000 Normal Closure.
 *   2. @aelive/dds-client's WsTransport counts EVERY close as a failure and
 *      sets status "reconnecting" — it never inspects the close code.
 *
 * With nothing subscribed there is no retry loop running, so "connected" never
 * fires again and the status is stuck. Worse, those phantom failures accumulate
 * and eight of them trip the circuit breaker to "offline" — a red banner with
 * nothing whatsoever wrong.
 *
 * The transport's status is therefore unfalsifiable when no one is subscribed:
 * it can only latch. So the app has to know whether anyone is actually
 * listening, and that is what this module provides. The banner then means what
 * an operator assumes it means — "live data YOU ARE RELYING ON is down" — rather
 * than "a socket closed at some point in this browser session".
 *
 * `ddsSubscribe` is the only sanctioned way to open a DDS subscription in this
 * app; a direct `dds.subscribe` bypasses the count and makes the banner lie by
 * omission. That is enforced by a test, not by convention alone.
 */

/**
 * token -> {label, startedAt}.
 *
 * A Map keyed by unique tokens rather than an integer counter: deleting a key
 * twice is structurally a no-op, so a double-unsubscribe cannot drive the count
 * negative and strand the banner in "nothing is live" forever.
 */
const active = new Map();
const listeners = new Set();
let lastNotified = 0;

function notify() {
  const n = active.size;
  // Value-deduped. Without this, N subscribers mounting together would emit N
  // identical notifications and re-render every consumer N times.
  if (n === lastNotified) return;
  lastNotified = n;
  listeners.forEach((cb) => {
    try {
      cb(n);
    } catch (e) {
      // One bad listener must not stop the others from being told.
      console.warn("[dds] subscription-count listener threw:", e);
    }
  });
}

export function getActiveSubscriptionCount() {
  return active.size;
}

/** Stable identity, so it is safe as a useSyncExternalStore subscribe fn. */
export function subscribeToActiveCount(cb) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Dev aid: what is open, and for how long. */
export function getActiveSubscriptionDebug() {
  return [...active.values()].map((v) => ({ ...v, ageMs: Date.now() - v.startedAt }));
}

/** Best-effort label from the operation name, so debug output is readable. */
const describe = (request) =>
  String(request?.query || "").match(/subscription\s+(\w+)/)?.[1] || "subscription";

/**
 * Open a DDS subscription and keep it counted.
 *
 * Signature is `dds.subscribe`'s, plus an options bag, so migrating a call site
 * is a rename and (optionally) one extra argument.
 *
 * @param {{query: string, variables?: object}} request
 * @param {(msg: any) => void} onData
 * @param {(err: any) => void} [onError]
 * @param {{label?: string}} [options]
 * @returns {() => void} unsubscribe
 */
export function ddsSubscribe(request, onData, onError, { label } = {}) {
  const token = Symbol("dds-sub");
  let released = false;

  /** @returns {boolean} true only for the call that actually released. */
  const release = () => {
    if (released) return false;
    released = true;
    active.delete(token);
    notify();
    return true;
  };

  active.set(token, { label: label || describe(request), startedAt: Date.now() });
  notify();
  wakeBreakerIfIdle();

  let dispose;
  try {
    dispose = dds.subscribe(request, onData, (err) => {
      // graphql-ws's sink.error TERMINATES the sequence — the transport evicts
      // its shared entry and the caller's own cleanup may never run for this
      // one. Releasing here is what stops the count leaking on a subscription
      // that no longer exists, which would latch the banner all over again.
      release();
      onError?.(err);
    });
  } catch (err) {
    // e.g. the client has no WS transport configured. Do not leave a token
    // behind for a subscription that was never opened.
    release();
    throw err;
  }

  return () => {
    // Release BEFORE disposing: a dispose that throws must not be able to
    // strand the refcount.
    //
    // Dispose ONLY on the call that actually released. The transport's dedupe
    // layer decrements a shared subscriber set, so disposing twice for one
    // subscribe could tear down a subscription another consumer still needs.
    // (Today it happens to guard against that itself — this does not depend on
    // that staying true.)
    if (!release()) return;
    try {
      dispose?.();
    } catch (e) {
      console.warn("[dds] unsubscribe failed:", e);
    }
  };
}

/**
 * Breaker hygiene on the 0 -> 1 edge.
 *
 * The transport's failure counter only resets after 30s of CONTINUOUS
 * connection, so the phantom closes described above accumulate across ordinary
 * navigation and can leave the breaker open with nothing actually broken. A
 * user opening a page that needs live data is the same class of "try now"
 * signal the transport already honours for `online` and `visibilitychange`.
 */
function wakeBreakerIfIdle() {
  if (active.size !== 1) return;
  try {
    if (dds?.connection?.getState?.()?.status === "offline") {
      dds.connection.reconnectNow?.();
    }
  } catch {
    // Never let a status probe break opening a subscription.
  }
}

/**
 * Dev-only sanity check that our count has not drifted from the transport's own
 * open server subscriptions.
 *
 * Deliberately WARN-ONLY, never a veto on what the banner shows. It reads
 * private-ish transport internals, and letting those silence a warning banner
 * would trade a visible false positive for an invisible false negative — the
 * strictly worse failure.
 */
export function warnOnSubscriptionDrift() {
  if (process.env.NODE_ENV === "production") return;
  if (active.size === 0) return;
  const ws = dds?.ws;
  if (!ws || ws.dedupeEnabled !== true) return;
  if (ws.sharedCount > 0) return;
  console.warn(
    "[dds] subscription refcount drift: app=%d transport=0",
    active.size,
    getActiveSubscriptionDebug(),
  );
}

/** Test seam. */
export function __resetSubscriptionRegistry() {
  active.clear();
  listeners.clear();
  lastNotified = 0;
}

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  window.__ddsSubscriptions = getActiveSubscriptionDebug;
}
